import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Mail, Link2, Trash2 } from "lucide-react";
import { InviteForm } from "@/components/team/InviteForm";

export function SirvientesTab({ members, onRefresh }) {
  const { authAxios } = useAuth();
  const sirvientes = members.filter(m => m.role === "sirviente");
  const [showForm, setShowForm] = useState(false);
  const [resending, setResending] = useState(null);

  const remove = async (id, name) => {
    if (!window.confirm(`Remover ${name} da equipa?`)) return;
    try {
      await authAxios.delete(`/deliverers/team/members/${id}`);
      toast.success("Membro removido");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  const resendInvite = async (m) => {
    setResending(m.id);
    try {
      await authAxios.post(`/deliverers/team/members/${m.id}/resend-invite`);
      toast.success(`Convite reenviado para ${m.email}`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao reenviar convite"); }
    setResending(null);
  };

  const copyInvite = (m) => {
    if (m.invite_token) {
      navigator.clipboard.writeText(`${window.location.origin}/invite/${m.invite_token}`);
      toast.success("Link copiado");
    }
  };

  return (
    <div data-testid="sirvientes-tab">
      <Button className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase mb-4" onClick={() => setShowForm(true)} data-testid="add-sirviente-btn">
        ADICIONAR ATENDENTE
      </Button>
      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-gray-500 text-xs uppercase">
              <th className="text-left p-3 font-medium">Nome</th>
              <th className="text-left p-3 font-medium">Email</th>
              <th className="text-center p-3 font-medium">Estado</th>
              <th className="p-3 w-32"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sirvientes.map(m => (
              <tr key={m.id} className="hover:bg-gray-50">
                <td className="p-3 font-medium">{m.name}</td>
                <td className="p-3 text-gray-600 text-sm">{m.email || "-"}</td>
                <td className="p-3 text-center">
                  {m.invite_accepted ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Ativo</Badge>
                  ) : m.invite_status === "sent" ? (
                    <Badge className="bg-blue-100 text-blue-700 text-[10px]">Enviado</Badge>
                  ) : m.invite_status === "error" ? (
                    <Badge className="bg-red-100 text-red-700 text-[10px]" title={m.invite_error}>Erro</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pendente</Badge>
                  )}
                </td>
                <td className="p-3 flex items-center gap-1">
                  {!m.invite_accepted && m.email && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600"
                      onClick={() => resendInvite(m)} disabled={resending === m.id} title="Reenviar convite">
                      {resending === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    </Button>
                  )}
                  {!m.invite_accepted && m.invite_token && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" onClick={() => copyInvite(m)} title="Copiar link">
                      <Link2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => remove(m.id, m.name)} title="Remover">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {sirvientes.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-gray-400">Sem atendentes</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold">Adicionar Atendente</DialogTitle>
          </DialogHeader>
          <InviteForm role="sirviente" onClose={() => setShowForm(false)} onRefresh={onRefresh} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
